'use client';

import Image from 'next/image';
import { LuCheckCheck, LuArrowRight } from 'react-icons/lu';
import { useRouter } from 'next/navigation';

const AboutSectionOne = () => {
  const router = useRouter();

  const achievements = [
    {
      stat: '10+',
      title: 'Years Experience',
      description: 'Delivering excellence in land and property services',
      gradient: 'from-[#00B0F0] to-[#0078A8]',
    },
    {
      stat: '20+',
      title: 'Projects Completed',
      description: 'Across diverse service categories',
      gradient: 'from-[#FFA500] to-[#FF7A00]',
    },
    {
      stat: '98%',
      title: 'Client Satisfaction',
      description: 'Based on continuous feedback',
      gradient: 'from-[#00B050] to-[#008040]',
    },
  ];

  const features = [
    {
      title: 'Professional Excellence',
      description:
        'Expert teams equipped with cutting-edge technology and industry best practices',
      icon: <LuCheckCheck className='h-6 w-6 text-white' />,
      gradient: 'from-[#00B0F0] to-[#0078A8]',
    },
    {
      title: 'Comprehensive Solutions',
      description:
        'Integrated services spanning land survey, GIS, property management, and more',
      icon: <LuCheckCheck className='h-6 w-6 text-white' />,
      gradient: 'from-[#FFA500] to-[#FF7A00]',
    },
    {
      title: 'Customer-Centric Approach',
      description:
        'Tailored solutions and dedicated support throughout your journey',
      icon: <LuCheckCheck className='h-6 w-6 text-white' />,
      gradient: 'from-[#00B050] to-[#008040]',
    },
    {
      title: 'Innovation & Technology',
      description:
        'Leveraging modern tools and techniques for precise, efficient service delivery',
      icon: <LuCheckCheck className='h-6 w-6 text-white' />,
      gradient: 'from-[#00B0F0] to-[#0078A8]',
    },
  ];

  const handleExploreServices = () => {
    // Navigate to the services page
    router.push('/services');
  };

  return (
    <section className='relative overflow-hidden bg-gray-900 py-24 text-white'>
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-10 bg-[url('/images/patterns/circuit-pattern.svg')] bg-repeat"></div>
      
      {/* Decorative accent line at top */}
      <div className='absolute top-0 left-0 right-0 h-1 bg-linear-to-r from-[#00B0F0] via-[#FFA500] to-[#00B050]'></div>

      <div className='container mx-auto px-4 relative z-10'>
        <div className='flex flex-col gap-16 lg:flex-row lg:gap-24'>
          {/* Left Column - Content */}
          <div className='lg:w-1/2'>
            <div className='mb-8'>
              <h2 className='mb-4 text-3xl font-bold lg:text-5xl'>
                Transforming Property Services with{' '}
                <span className='relative inline-block'>
                  <span className='absolute -inset-1 blur-xl bg-[#00B0F0] opacity-30'></span>
                  <span className='relative bg-linear-to-r from-[#00B0F0] to-[#0078A8] bg-clip-text text-transparent'>
                    Innovation
                  </span>
                </span>{' '}
                and{' '}
                <span className='relative inline-block'>
                  <span className='absolute -inset-1 blur-xl bg-[#00B050] opacity-30'></span>
                  <span className='relative bg-linear-to-r from-[#00B050] to-[#008040] bg-clip-text text-transparent'>
                    Excellence
                  </span>
                </span>
              </h2>
              <p className='text-lg text-gray-300'>
                At Qwetulands, we combine technical expertise with innovative
                solutions to deliver comprehensive property and land services.
                Our commitment to excellence and customer satisfaction drives
                everything we do.
              </p>
            </div>

            {/* Features Grid */}
            <div className='mb-12 grid gap-6 sm:grid-cols-2'>
              {features.map((feature, index) => (
                <div
                  key={index}
                  className='group relative overflow-hidden rounded-xl bg-gray-800/50 p-6 backdrop-blur-xs transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#00B0F0]/10 border border-gray-700/50'
                >
                  {/* Feature gradient accent */}
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-linear-to-r ${feature.gradient}`}></div>
                  
                  <div className='flex items-start gap-4'>
                    <div className={`mt-1 shrink-0 rounded-full p-2 bg-linear-to-r ${feature.gradient}`}>
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className='mb-2 font-semibold text-white'>
                        {feature.title}
                      </h3>
                      <p className='text-sm text-gray-300'>
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <button
              onClick={handleExploreServices}
              className='group relative inline-flex items-center rounded-full bg-linear-to-r from-[#00B0F0] to-[#0078A8] px-8 py-4 text-white transition-all duration-300 hover:shadow-lg hover:shadow-[#00B0F0]/25'
            >
              <span className='font-medium'>Explore Our Services</span>
              <LuArrowRight className='ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1' />
              <div className='absolute inset-0 rounded-full bg-white/10 animate-pulse-slow -z-10'></div>
            </button>
          </div>

          {/* Right Column - Image & Stats */}
          <div className='lg:w-1/2'>
            <div className='relative'>
              {/* Main Image */}
              <div className='relative h-[500px] overflow-hidden rounded-2xl shadow-2xl'>
                <Image
                  src='/images/about-image-2.jpg'
                  alt='Qwetulands Services'
                  fill
                  className='object-cover brightness-[0.85]'
                />
                {/* Overlay Gradient */}
                <div className='absolute inset-0 bg-linear-to-t from-gray-900/80 to-transparent' />
                
                {/* Subtle pattern overlay */}
                <div className="absolute inset-0 opacity-20 mix-blend-overlay bg-[url('/images/patterns/circuit-pattern.svg')] bg-repeat bg-cover"></div>
              </div>

              {/* Stats Cards */}
              <div className='absolute -bottom-8 left-4 right-4'>
                <div className='grid grid-cols-3 gap-4'>
                  {achievements.map((achievement, index) => (
                    <div
                      key={index}
                      className='group relative overflow-hidden rounded-xl bg-gray-800/80 backdrop-blur-xs p-4 shadow-lg transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg border border-gray-700/50'
                    >
                      {/* Card gradient accent */}
                      <div className={`absolute top-0 left-0 right-0 h-1 bg-linear-to-r ${achievement.gradient}`}></div>
                      
                      <div className={`text-2xl font-bold bg-linear-to-r ${achievement.gradient} bg-clip-text text-transparent`}>
                        {achievement.stat}
                      </div>
                      <div className='text-sm font-medium text-white'>
                        {achievement.title}
                      </div>
                      <div className='mt-1 text-xs text-gray-300'>
                        {achievement.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Decorative Elements */}
              <div className='absolute -right-12 -top-12 h-24 w-24 rounded-full bg-[#00B050]/20 blur-xl' />
              <div className='absolute -left-8 bottom-24 h-16 w-16 rounded-full bg-[#00B0F0]/20 blur-xl' />
              <div className='absolute right-20 top-20 h-12 w-12 rounded-full bg-[#FFA500]/20 blur-xl' />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSectionOne;
